import { ApiError, request } from './api';

/**
 * The step-up endpoints, and the words for what can go wrong at them.
 *
 * Separate from `api.ts` so that the dialog can use `request` without
 * `api.ts` importing the dialog — the handler registration already runs the
 * other way.
 */

export type StepUpMethod = 'authenticator' | 'email';

export type StepUpStarted =
	{ readonly method: 'authenticator' } | { readonly method: 'email'; readonly sent: boolean };

export function startStepUp(): Promise<StepUpStarted> {
	return request('/api/auth/step-up/start', { method: 'POST' });
}

export function submitStepUp(code: string): Promise<void> {
	return request('/api/auth/step-up', { method: 'POST', body: { code: code.trim() } });
}

export interface Authenticator {
	readonly id: string;
	readonly name: string;
	readonly createdAt: number;
	readonly lastUsedAt?: number;
}

export function listAuthenticators(): Promise<{ authenticators: Authenticator[] }> {
	return request('/api/me/authenticators');
}

export function beginEnrolment(): Promise<{ secret: string; uri: string }> {
	return request('/api/me/authenticators', { method: 'POST' });
}

export function confirmEnrolment(name: string, code: string): Promise<Authenticator> {
	return request('/api/me/authenticators/confirm', {
		method: 'POST',
		body: { name: name.trim(), code: code.trim() },
	});
}

export function removeAuthenticator(id: string): Promise<void> {
	return request(`/api/me/authenticators/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * The refusals a person can act on, in their own terms.
 *
 * `wrong` deliberately does not say which of the two methods was checked —
 * the dialog already said which one to use, and repeating it in the error
 * reads as a hint that the other might work.
 */
const REFUSALS: Record<string, string> = {
	wrong: 'That code is not right. Check it and try again.',
	expired: 'That code has expired. Close this and try again to get a new one.',
	none: 'There is no code waiting. Close this and try again.',
	exhausted: 'Too many attempts. Close this and try again to get a new code.',
	'rate-limited': 'Too many attempts. Wait a few minutes and try again.',
	gone: 'You have been signed out. Sign in again to continue.',
	invalid_request: 'A code is six digits.',
};

export function explainStepUp(error: unknown): string {
	if (!(error instanceof ApiError)) return 'That did not work. Try again.';
	return REFUSALS[error.code] ?? 'That did not work. Try again.';
}
